import React, { useState, useEffect } from 'react';
import { Box, Paper } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useGridApiRef } from '@mui/x-data-grid-premium';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import RssFeedIcon from '@mui/icons-material/RssFeed';
import PageHeader from '../../components/PageHeader/PageHeader';
import StyledDataGrid from '../../components/DataGrid/DataGridStyles';
import AutosizeColumnsToggle from '../../components/DataGrid/AutosizeColumnsToggle';
import Toolbar from '../../components/Toolbar/Toolbar';
import ConfirmationModal from '../../components/Modal/ConfirmationModal';
import EmptyState from '../../components/EmptyState/EmptyState';
import { useTableSearch } from '../../hooks/useTableSearch';
import { useDataFeedData } from './hooks/useDataFeedData';
import { useDataFeedColumns } from './hooks/useDataFeedColumns';
import { DataFeedRow } from './types';
import dataFeedWizardStore from '../../store/dataFeedWizard/dataFeedWizardStore';
import Actions from '../../store/Actions';

interface SessionToDelete {
  sessionId: string;
  fileName: string;
}

export const DataFeedNewPage: React.FC = () => {
  const navigate = useNavigate();
  const apiRef = useGridApiRef();

  // Delete modal state
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [sessionToDelete, setSessionToDelete] =
    useState<SessionToDelete | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  // Hooks
  const { data, loading, refetch, autosizeColumns, setAutosizeColumns } =
    useDataFeedData({ apiRef });
  const columns = useDataFeedColumns({
    onDelete: (sessionId, fileName) => {
      setSessionToDelete({ sessionId, fileName });
      setDeleteModalOpen(true);
    },
  });
  const { filteredData, searchTerm, handleSearch } =
    useTableSearch<DataFeedRow>(data);

  // Reset wizard state when navigating to this page
  useEffect(() => {
    dataFeedWizardStore.getState().resetWizard();
  }, []);

  // Handlers
  const handleUploadClick = (): void => {
    dataFeedWizardStore.getState().resetWizard();
    navigate('/data-feed-wizard');
  };

  const handleCloseDeleteModal = () => {
    setDeleteModalOpen(false);
    setSessionToDelete(null);
  };

  const handleConfirmDelete = async () => {
    if (!sessionToDelete) return;

    setIsDeleting(true);
    try {
      await Actions.deleteWizardSession(sessionToDelete.sessionId);
      handleCloseDeleteModal();
      refetch();
    } catch (error) {
      console.error('Failed to delete session:', error);
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <Box
      data-testid="data-feed-page"
      sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}
    >
      <PageHeader
        title="Data Feeds"
        subtitle="Manage your uploaded data feeds"
      />

      <Paper
        data-testid="data-feed-content"
        sx={{ padding: 2, flex: 1, display: 'flex', flexDirection: 'column' }}
      >
        <Toolbar
          searchTerm={searchTerm}
          onSearch={handleSearch}
          placeholder="Search data feeds..."
          buttons={[
            {
              variant: 'contained',
              label: 'Upload Feed',
              onClick: handleUploadClick,
              icon: <CloudUploadIcon />,
            },
          ]}
        />

        <Box sx={{ display: 'flex', justifyContent: 'flex-start' }}>
          <AutosizeColumnsToggle
            checked={autosizeColumns}
            onChange={setAutosizeColumns}
          />
        </Box>

        <Box
          data-testid="data-feed-grid-container"
          sx={{ height: 'calc(100vh - 260px)', width: '100%' }}
        >
          {!loading && filteredData.length === 0 ? (
            <EmptyState
              icon={
                <RssFeedIcon sx={{ fontSize: 80, color: 'text.secondary' }} />
              }
              title="No Data Feeds"
              description="Upload your first data feed to begin processing transactions"
              actions={[
                {
                  label: 'Upload Feed',
                  onClick: handleUploadClick,
                  variant: 'contained',
                },
              ]}
            />
          ) : (
            <StyledDataGrid
              data-testid="data-feed-grid"
              apiRef={apiRef}
              rows={filteredData}
              columns={columns}
              loading={loading}
            />
          )}
        </Box>
      </Paper>

      <ConfirmationModal
        open={deleteModalOpen}
        title="Delete Data Feed Session"
        message={`Are you sure you want to delete this session and its file "${sessionToDelete?.fileName}"? This action cannot be undone.`}
        confirmText="Delete"
        cancelText="Cancel"
        onConfirm={handleConfirmDelete}
        onCancel={handleCloseDeleteModal}
        severity="error"
        submitDisabled={isDeleting}
        submitLoading={isDeleting}
      />
    </Box>
  );
};
